/* ONE BLOCK — the row every composed record in Momentum is made of (spec R1,
 * build step 3, and item 5 on her list, 2026-09-16).
 *
 * A website section, a course block and (after the Outreach redesign) a touch
 * are the same object: a typed set of fields inside a record. Each panel used
 * to draw its own, and they drifted — one opened to a read view and one to
 * fields, one had a sparkle everywhere and one only sometimes, one had a pencil
 * that vanished when the row was open. Her call: "website is the most
 * complete … mirror that … use that for everything … or we will keep playing
 * wackamole."
 *
 * SO THIS IS THE WEBSITE SECTION'S ROW, LIFTED OUT UNCHANGED IN BEHAVIOUR:
 *   · collapsed by default — a header strip: grip, chevron, the kind, how much
 *     is written. The whole strip opens and closes it (rule 2).
 *   · open shows the copy as READING, not fields (decision 7). Clicking the copy
 *     or the pencil switches to the fields.
 *   · four actions on every row whether or not it has words (rule 3): sparkle
 *     (Brill drafts it in place), chat (the drafting screen), pencil (edit it
 *     yourself), bin (asks first — the panel's ConfirmDialog, not this file).
 *   · editing: fields save as she leaves each one (rule 1); Cancel puts her
 *     words back; Save closes the fields.
 *   · a sparkle in flight says so, in the row.
 *
 * NOTHING HERE KNOWS WHAT A PAGE OR A MODULE IS. It is handed a kind (label and
 * fields), the stored content, and what to call when something happens. What
 * only a website section has — the kind picker, "From My Website", word
 * budgets, copy a kind hides — comes in through the slots below from
 * sections.jsx; what only a course block has — its minutes — from course.jsx.
 */
import { useState } from "react";
import { ChevronRight, MessageSquare, Pencil, Sparkles, Trash2 } from "lucide-react";
import { SectionFields, SectionRead } from "./fields";
import { B, nb } from "./tokens";
import { IconBtn } from "./ui";

/* "Write with Brill". The one action in the cluster that isn't flat, because it
   is the one she is being invited to press on an empty row.

   Teal, on her call: in this app amber MEANS something (draft, stale, needs
   attention) and spending it on an invitation reads as a warning about the row
   rather than an offer.

   `disabled` + `label` say WHY it can't be pressed yet (a course block drafts
   from the module's outcome, so there has to be one). Still drawn, so the row
   keeps its actions (rule 3). */
export function GenerateBadge({ onClick, label, busy, disabled }) {
  const [on, setOn] = useState(false);
  const off = busy || disabled;
  return (
    <button
      onClick={off ? undefined : onClick} disabled={off} title={label} aria-label={label}
      onMouseEnter={() => setOn(true)} onMouseLeave={() => setOn(false)}
      style={{
        width: 24, height: 24, padding: 0, borderRadius: 999,
        border: `1px solid ${disabled ? B.border2 : B.tealLine}`,
        background: on && !off ? B.teal : disabled ? "transparent" : B.tealQuiet,
        color: on && !off ? "var(--btn-text)" : disabled ? B.ghost : B.teal,
        display: "flex", alignItems: "center", justifyContent: "center",
        /* Dimmed while the call is out. One round trip takes a few seconds with
           nothing else on screen changing, so without this the only thing to do
           is press it again — and a second press is a second API call. */
        cursor: off ? "default" : "pointer", flexShrink: 0, opacity: busy ? 0.5 : 1,
      }}
    >
      <Sparkles size={13} />
    </button>
  );
}

/**
 * @param kind         { id, label, fields } — what the fields are
 * @param content      the stored content of this block
 * @param words        (content) => how many words are in it
 * @param onCommit     (content) => write it. Called on the way out of a field
 *                     and on Save, only when something changed.
 * @param snapshot     () => what "before" means when she opens the editor.
 *                     Defaults to the stored content.
 * @param onCancel     (before) => undo the session; returns the content to put
 *                     back on screen, or null when there was nothing to undo.
 *                     Defaults to writing `before` back.
 * @param sparkle      { onClick, busy, disabled, label } — omit for no sparkle
 * @param onChat       open the drafting screen on this block
 * @param onClear      the bin. The PANEL confirms first.
 * @param clearLabel   what the bin says ("Delete section", "Clear outcome")
 * @param noun         "section", "block" — used in the row's own words
 * @param badges       extra things beside the word count (a course's minutes);
 *                     may be a function of the content on screen
 * @param tag          a label after them ("From My Website")
 * @param editTop      shown above the fields while editing (the kind picker);
 *                     a function gets (content, change, commit) — change(next)
 *                     sets the open fields without saving, change(next, true)
 *                     saves at once, commit() saves what is on screen (a blur)
 * @param editBottom   shown below them (a notice about hidden copy). Either may
 *                     be a function of the content on screen.
 * @param fieldProps   passed to SectionFields (word budgets)
 * @param startOpen    open on mount — Brill just wrote it
 */
export function Block({
  kind, content: stored, words: wordsOf, onCommit, snapshot, onCancel,
  sparkle, onChat, onClear, clearLabel, noun = "section",
  badges = null, tag = null, editTop = null, editBottom = null, readBottom = null, fieldProps = {},
  startOpen, dragHandle, dragging,
}) {
  const [content, setContent] = useState(stored || {});
  /* Open when Brill has just written it — the words she is waiting for must not
     land inside a closed row. Set once, from the remount the caller forces. */
  const [open, setOpen] = useState(!!startOpen);
  const [editing, setEditing] = useState(false);
  /* The block as it stood when she opened the editor. It is the ONLY answer to
     "what did this say before?" — the fields write on blur, so the stored row
     has already moved on by the time Cancel is pressed. */
  const [before, setBefore] = useState(null);
  const words = wordsOf ? wordsOf(content) : 0;
  const hasCopy = words > 0;
  const sparkling = !!sparkle?.busy;
  const label = kind.label.toLowerCase();

  const commit = (next = content) => {
    if (JSON.stringify(next) === JSON.stringify(stored || {})) return;
    onCommit(next);
  };
  const change = (next, immediate) => {
    setContent(next);
    if (immediate) commit(next);
  };

  /* The chevron is a 12px target, so the header strip and the open drawer carry
     it too: the strip opens and closes, the drawer goes straight to the
     fields. One `closest` check rather than stopPropagation on every child. */
  const onChrome = (e) => !e.target.closest("button, select, input, textarea, a, label");
  const headerClick = (e) => { if (!editing && onChrome(e)) setOpen((v) => !v); };
  const bodyClick = (e) => {
    if (editing || !onChrome(e)) return;
    // Selecting a line of her copy to read it shouldn't fling her into the editor.
    if (String(window.getSelection?.() || "").trim()) return;
    edit();
  };

  const edit = () => {
    setBefore(snapshot ? snapshot() : (stored || {}));
    setOpen(true);
    setEditing(true);
  };
  const cancel = () => {
    if (onCancel) {
      const back = onCancel(before);
      if (back) setContent(back);
    } else if (before && JSON.stringify(before) !== JSON.stringify(content)) {
      setContent(before);
      onCommit(before);
    }
    setBefore(null);
    setEditing(false);
  };
  const save = () => { commit(); setEditing(false); };

  return (
    <div style={{ opacity: dragging ? 0.4 : 1 }}>
      {/* Header */}
      <div onClick={headerClick} style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px 8px 8px",
        border: `1px solid ${B.border}`,
        borderRadius: open ? "10px 10px 0 0" : 10,
        background: open ? B.surface : B.surface2,
        cursor: editing ? "default" : "pointer",
        /* Or a long name makes the row itself wider than the screen instead of
           being cut short inside it — see the label below. */
        minWidth: 0,
      }}>
        {dragHandle}
        <button onClick={() => setOpen((v) => !v)} aria-label={open ? `Collapse ${noun}` : `Expand ${noun}`} aria-expanded={open}
          style={{ width: 18, height: 18, padding: 0, border: "none", background: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: B.mid, flexShrink: 0 }}>
          <ChevronRight size={12} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 150ms ease" }} />
        </button>
        {/* IT SHRINKS, AND IT ENDS IN AN ELLIPSIS. It used to be `flexShrink: 0`
            — fine while a label was two words ("BANNER", "SERVICES"), and not
            fine once Presentations started writing the slide's own title into
            it: "SINGLE-POINT STATEMENT · YOU ARE NOT AFRAID OF THE DARK…". A
            flex item that cannot shrink does not get cut off, it makes the ROW
            wider than the phone, and the row's own actions go over the edge
            with it. Her report, 2026-09-18: "mobile sections on presentations
            at least aren't protected with a ... from running off the page."

            Every composed row in Momentum is this one, so every panel was one
            long name away from the same thing. */}
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: hasCopy ? B.mid : B.faint,
          minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {kind.label}
        </span>
        {typeof badges === "function" ? badges(content) : badges}
        <span style={{ fontSize: 11, color: sparkling ? B.teal : B.faint, fontStyle: hasCopy && !sparkling ? "normal" : "italic", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {/* Said here ONLY WHEN COLLAPSED — open, the body says it, and two
              things animating on one row is one too many (her report,
              2026-09-15). */}
          {sparkling && !open ? "Making magic…" : hasCopy ? `${words} ${words === 1 ? "word" : "words"}` : "Not written yet"}
        </span>
        {tag}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          {editing ? (
            <>
              <button onClick={cancel} style={{ padding: "5px 11px", borderRadius: 999, border: `1px solid ${B.border2}`, background: "none", fontSize: 11.5, fontWeight: 600, color: B.mid, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={save} style={{ padding: "5px 13px", borderRadius: 999, border: "none", background: "var(--btn-bg)", color: "var(--btn-text)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
            </>
          ) : (
            <>
              {/* TWO DOORS, AND THEY ARE DIFFERENT THINGS. The SPARKLE is one
                  click: Brill drafts this block and it lands in the fields, and
                  she stays where she is. The CHAT is the brainstorm. */}
              {sparkle && (
                <GenerateBadge onClick={sparkle.onClick} busy={sparkling} disabled={sparkle.disabled}
                  label={sparkling ? "Brill is writing this…" : sparkle.label || (hasCopy ? "Rewrite with Brill" : "Write with Brill")} />
              )}
              {onChat && <IconBtn icon={MessageSquare} size={14} label="Edit in chat" onClick={onChat} />}
              {/* An empty row needs the pencil MOST — it is the only way to type
                  her own copy rather than have Brill write it. */}
              <IconBtn icon={Pencil} label={hasCopy ? "Edit copy" : "Write it yourself"} onClick={edit} />
              {onClear && <IconBtn icon={Trash2} label={clearLabel || `Delete ${noun}`} hover="var(--danger-text)" onClick={onClear} />}
            </>
          )}
        </div>
      </div>

      {/* Body */}
      {open && (
        <div onClick={bodyClick} title={editing ? undefined : "Click to edit"}
          style={{ border: `1px solid ${B.border}`, borderTop: "none", borderRadius: "0 0 10px 10px", background: B.surface, padding: editing ? "16px 16px 4px" : "14px 16px", cursor: editing ? "default" : "pointer" }}>
          {editing ? (
            <>
              {typeof editTop === "function" ? editTop(content, change, () => commit()) : editTop}
              <SectionFields kind={kind} content={content} onChange={change} onBlur={() => commit()} {...fieldProps} />
              {typeof editBottom === "function" ? editBottom(content) : editBottom}
            </>
          ) : sparkling ? (
            /* What a press looks like while it is working — a dimmed 24px icon
               alone reads as nothing happening. */
            <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, color: B.teal, lineHeight: 1.6 }}>
              <Sparkles size={14} style={{ flexShrink: 0 }} />
              Making magic — Brill is writing your {label}…
            </div>
          ) : hasCopy ? (
            <>
              <SectionRead kind={kind} content={content} />
              {/* READ-SIDE ACTIONS for what only this record has (an email's
                  Copy, 2026-10-12). Clicks stay here: the drawer around them
                  opens the editor on a click. */}
              {readBottom && <div onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>{readBottom}</div>}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: B.dim, lineHeight: 1.6 }}>
              Nothing here yet.{" "}
              {sparkle && !sparkle.disabled
                ? <>Press the <b style={nb}>sparkle</b> and Brill writes it with you, or the <b style={nb}>pencil</b> to type it yourself.</>
                : <>Press the <b style={nb}>pencil</b> to type it yourself{onChat ? <>, or the <b style={nb}>chat</b> to write it with Brill</> : null}.</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
